import { useState } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Info, FileText } from "lucide-react";

interface MergeField {
  field: string;
  description: string;
}

interface MarkdownTip {
  syntax: string;
  result: string;
}

const MARKDOWN_TIPS: MarkdownTip[] = [
  { syntax: "**bold**", result: "Bold text" },
  { syntax: "*italic*", result: "Italic text" },
  { syntax: "# Heading", result: "Heading (##, ### for sub-headings)" },
  { syntax: "- item", result: "Unordered list item" },
  { syntax: "1. item", result: "Ordered list item" },
  { syntax: "[text](url)", result: "Hyperlink" },
  { syntax: "---", result: "Horizontal rule" },
];

interface MarkdownBodyEditorProps {
  value: string;
  onChange: (value: string) => void;
  mergeFields?: MergeField[];
  placeholder?: string;
  id?: string;
  testIdPrefix?: string;
}

export function MarkdownBodyEditor({
  value,
  onChange,
  mergeFields,
  placeholder = "Enter email template with merge fields and Markdown formatting...",
  id,
  testIdPrefix = "",
}: MarkdownBodyEditorProps) {
  const [tab, setTab] = useState<"edit" | "preview">("edit");

  const previewHtml = value
    ? DOMPurify.sanitize(
        marked.parse(value, { async: false }) as string,
        { USE_PROFILES: { html: true } }
      )
    : '<p style="color: var(--muted-foreground); font-style: italic;">Nothing to preview — switch to Edit and add some content.</p>';

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={id}>Email Body</Label>
          <div className="flex rounded-md border overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setTab("edit")}
              className={`px-3 py-1 transition-colors ${
                tab === "edit"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:bg-muted"
              }`}
              data-testid={`${testIdPrefix}tab-body-edit`}
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setTab("preview")}
              className={`px-3 py-1 transition-colors ${
                tab === "preview"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:bg-muted"
              }`}
              data-testid={`${testIdPrefix}tab-body-preview`}
            >
              Preview
            </button>
          </div>
        </div>

        {tab === "edit" ? (
          <Textarea
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={12}
            className="font-mono text-sm"
            placeholder={placeholder}
            data-testid={`${testIdPrefix}input-email-body`}
          />
        ) : (
          <div
            className="min-h-[200px] rounded-md border bg-background px-3 py-2 text-sm prose prose-sm max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
            data-testid={`${testIdPrefix}preview-email-body`}
          />
        )}
      </div>

      {mergeFields && mergeFields.length > 0 && (
        <div className="bg-muted p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Info className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Available Merge Fields</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {mergeFields.map((f) => (
              <div key={f.field} className="flex gap-2">
                <code className="bg-background px-2 py-0.5 rounded text-xs shrink-0">
                  {f.field}
                </code>
                <span className="text-muted-foreground">{f.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-muted p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Markdown Quick Reference</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {MARKDOWN_TIPS.map((tip) => (
            <div key={tip.syntax} className="flex gap-2 items-baseline">
              <code className="bg-background px-2 py-0.5 rounded text-xs shrink-0">
                {tip.syntax}
              </code>
              <span className="text-muted-foreground">{tip.result}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
